import React from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

/**
 * Die uebersetzte Abfrage zu einer Kohorte -- anzeigen und herunterladen
 * (#796, Frontend-Haelfte von #789).
 *
 * **Warum das Anzeigen einen eigenen Platz bekommt und nicht am Ausfuehren
 * haengt.** Der Endpunkt uebersetzt nur; er beruehrt keine Zieldatenbank
 * (#789 AK1). Auf hospital1 antwortet `execute` derzeit mit HTTP 503, weil
 * dort keine OMOP-Quelle konfiguriert ist -- die Abfrage laesst sich trotzdem
 * zeigen und herunterladen. Waere die Anzeige an einen gelungenen Lauf
 * gekoppelt, waere genau das nicht moeglich.
 *
 * **Unuebersetzbare Kriterien stehen daneben, nicht statt der Abfrage.** Eine
 * Abfrage ohne den Hinweis, dass ein Kriterium fehlt, sieht aus, als seien
 * alle angewandt worden; ein blosser Fehler ohne die Abfrage verschweigt, was
 * immerhin uebersetzt wurde. Beides zusammen ist die ehrliche Anzeige.
 *
 * **Das Backend fuer die Abfrage ist ein eigener Umschalter, nicht der der
 * Ausfuehrung** (Review Runde 1, B1). Der Ausfuehrungs-Umschalter sperrt
 * `openehr` bis #E7.5 (fehlende openEHR-Ausfuehrung) -- die Uebersetzung kann
 * beide Backends aber schon heute, siehe die gemessene openEHR-Antwort in
 * #796.
 */
const UnuebersetzbarListe = ({ eintraege, testId }) => (
  <Box sx={{ mt: 0.75 }} data-testid={testId}>
    {eintraege.map((eintrag, index) => (
      <Typography key={index} variant="caption" color="warning.main" component="div">
        {eintrag?.kriterium?.parameterName || eintrag?.kriterium?.parameter_name || 'Criterion'}:{' '}
        {eintrag?.grund || 'not translatable'}
      </Typography>
    ))}
  </Box>
);

const CohortQueryPanel = ({
  hasActiveCohort,
  queryBackend = 'omop',
  onSetQueryBackend,
  queryResult,
  queryError,
  isLoadingQuery,
  onShowQuery,
  onDownloadQuery,
}) => (
  <Box sx={{ mt: 1.5 }} data-testid="kohorten-abfrage">
    <Stack direction="row" spacing={1} alignItems="center">
      <ToggleButtonGroup
        size="small"
        exclusive
        value={queryBackend}
        onChange={(_, next) => {
          if (next) onSetQueryBackend?.(next);
        }}
        aria-label="Query backend"
      >
        <ToggleButton value="omop" aria-label="Query OMOP">
          OMOP
        </ToggleButton>
        <ToggleButton value="openehr" aria-label="Query openEHR">
          openEHR
        </ToggleButton>
      </ToggleButtonGroup>
      <Button
        size="small"
        variant="outlined"
        disabled={!hasActiveCohort || isLoadingQuery}
        onClick={() => onShowQuery?.(queryBackend)}
        data-testid="abfrage-anzeigen"
      >
        Show query
      </Button>
      <Button
        size="small"
        variant="text"
        disabled={!hasActiveCohort || isLoadingQuery}
        onClick={() => onDownloadQuery?.(queryBackend)}
        data-testid="abfrage-herunterladen"
      >
        Download
      </Button>
      <Typography variant="caption" color="text.secondary">
        translates only -- no execution
      </Typography>
    </Stack>

    {queryError ? (
      // Der 422-Fall des Downloads (#794) und jeder andere Fehlschlag. Die
      // Begruendung des Servers steht woertlich da: sie benennt das Kriterium,
      // nicht die Anwendung. `unuebersetzbar` steht daneben (Review Runde 1,
      // B4) -- sonst bleibt ungenannt, welches Kriterium betroffen ist.
      <Alert severity="warning" sx={{ mt: 1 }} data-testid="abfrage-fehler">
        {queryError.detail}
        {queryError.unuebersetzbar?.length > 0 ? (
          <UnuebersetzbarListe eintraege={queryError.unuebersetzbar} testId="abfrage-fehler-unuebersetzbar" />
        ) : null}
      </Alert>
    ) : null}

    {queryResult ? (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {queryResult.sprache ? `${queryResult.sprache.toUpperCase()} · ` : ''}
          {queryResult.uebersetzt} criteria translated
          {queryResult.unuebersetzbar.length > 0
            ? `, ${queryResult.unuebersetzbar.length} not translatable`
            : ''}
        </Typography>
        <Paper
          variant="outlined"
          sx={{ mt: 0.5, p: 1, maxHeight: 200, overflow: 'auto', bgcolor: 'background.default' }}
        >
          <Typography
            component="pre"
            variant="caption"
            sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
            data-testid="abfrage-text"
          >
            {queryResult.abfrage}
          </Typography>
        </Paper>
        {queryResult.unuebersetzbar.length > 0 ? (
          <UnuebersetzbarListe eintraege={queryResult.unuebersetzbar} testId="abfrage-unuebersetzbar" />
        ) : null}
      </Box>
    ) : null}
  </Box>
);

export default CohortQueryPanel;
